/**
 * Minimal DOCX text extraction (OOXML zip → word/document.xml).
 * Uses pako (already in the app) — no mammoth / external API.
 */

import { inflateRaw } from 'pako';

function readU16(view: DataView, offset: number): number {
    return view.getUint16(offset, true);
}

function readU32(view: DataView, offset: number): number {
    return view.getUint32(offset, true);
}

function findLocalFile(buffer: ArrayBuffer, fileName: string): Uint8Array | null {
    const view = DataView ? new DataView(buffer) : null;
    if (!view) return null;
    const bytes = new Uint8Array(buffer);
    let offset = 0;

    while (offset + 30 < bytes.length) {
        const sig = readU32(view, offset);
        if (sig !== 0x04034b50) break; // local file header

        const compression = readU16(view, offset + 8);
        const compSize = readU32(view, offset + 18);
        const nameLen = readU16(view, offset + 26);
        const extraLen = readU16(view, offset + 28);
        const nameStart = offset + 30;
        const nameEnd = nameStart + nameLen;
        const entryName = new TextDecoder().decode(bytes.subarray(nameStart, nameEnd));
        const dataStart = nameEnd + extraLen;
        const dataEnd = dataStart + compSize;

        if (entryName === fileName) {
            const compressed = bytes.subarray(dataStart, dataEnd);
            if (compression === 0) return compressed;
            if (compression === 8) {
                try {
                    return inflateRaw(compressed);
                } catch {
                    return null;
                }
            }
            return null;
        }

        offset = dataEnd;
        // Some writers use data descriptor; if sizes were 0, stop — uncommon for docx document.xml
        if (compSize === 0 && entryName !== fileName) {
            // Fall through carefully: without size we can't skip reliably
            break;
        }
    }

    // Fallback: scan central directory for offsets (more reliable)
    return findViaCentralDirectory(buffer, fileName);
}

function findViaCentralDirectory(buffer: ArrayBuffer, fileName: string): Uint8Array | null {
    const view = new DataView(buffer);
    const bytes = new Uint8Array(buffer);
    let eocd = -1;
    for (let i = bytes.length - 22; i >= 0; i -= 1) {
        if (readU32(view, i) === 0x06054b50) {
            eocd = i;
            break;
        }
    }
    if (eocd < 0) return null;

    const cdOffset = readU32(view, eocd + 16);
    let offset = cdOffset;

    while (offset + 46 < bytes.length) {
        if (readU32(view, offset) !== 0x02014b50) break;
        const compression = readU16(view, offset + 10);
        const compSize = readU32(view, offset + 20);
        const nameLen = readU16(view, offset + 28);
        const extraLen = readU16(view, offset + 30);
        const commentLen = readU16(view, offset + 32);
        const localHeaderOffset = readU32(view, offset + 42);
        const nameStart = offset + 46;
        const entryName = new TextDecoder().decode(bytes.subarray(nameStart, nameStart + nameLen));

        if (entryName === fileName) {
            const lhNameLen = readU16(view, localHeaderOffset + 26);
            const lhExtraLen = readU16(view, localHeaderOffset + 28);
            const dataStart = localHeaderOffset + 30 + lhNameLen + lhExtraLen;
            const compressed = bytes.subarray(dataStart, dataStart + compSize);
            if (compression === 0) return compressed;
            if (compression === 8) {
                try {
                    return inflateRaw(compressed);
                } catch {
                    return null;
                }
            }
            return null;
        }

        offset = nameStart + nameLen + extraLen + commentLen;
    }

    return null;
}

function xmlToPlainText(xml: string): string {
    return xml
        .replace(/<w:tab[^/]*\/>/g, '\t')
        .replace(/<w:br[^/]*\/>/g, '\n')
        .replace(/<\/w:p>/g, '\n')
        .replace(/<[^>]+>/g, '')
        .replace(/&amp;/g, '&')
        .replace(/&lt;/g, '<')
        .replace(/&gt;/g, '>')
        .replace(/&quot;/g, '"')
        .replace(/&#39;/g, "'")
        .replace(/[ \t]+\n/g, '\n')
        .replace(/\n{3,}/g, '\n\n')
        .trim();
}

export async function extractTextFromDocx(file: File): Promise<string> {
    const buffer = await file.arrayBuffer();
    const xmlBytes = findLocalFile(buffer, 'word/document.xml');
    if (!xmlBytes) {
        throw new Error('Could not read this Word document. Try exporting as PDF or create a resume manually.');
    }
    const xml = new TextDecoder('utf-8').decode(xmlBytes);
    return xmlToPlainText(xml);
}
